import React, { useEffect, useState } from 'react';
import { Route, Routes, NavLink, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import Home from './pages/Home';
import PricingTool from './pages/PricingTool';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import ComingSoon from './pages/ComingSoon';
import NotFound from './pages/NotFound';
import Designer from './pages/Designer';
import ProtectedRoute from './components/ProtectedRoute';
import logo from '../src/components/files/comet-logo.png';
import { loginRequest } from './config/authConfig';
import { Sun, Moon } from 'lucide-react';

// ---------------------------------------------------------------------------
// Dark-mode: reads/writes the `dark` class on <html> and persists in localStorage
// ---------------------------------------------------------------------------
const useDarkMode = () => {
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem('comet_dark_mode');
    return stored === 'true' || (stored === null && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add('dark');
    else root.classList.remove('dark');
    localStorage.setItem('comet_dark_mode', dark);
  }, [dark]);

  return [dark, setDark];
};

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------
const Navigation = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [dark, setDark] = useDarkMode();

  const handleLogin = async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (e) {
      console.error("Login error:", e);
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: `${window.location.origin}/home` }).catch((e) => {
      console.error("Logout error:", e);
    });
  };

  const linkClass = ({ isActive }) =>
    isActive ? "font-bold border-b-2 border-white pb-1" : "hover:text-teal-200";

  return (
    <nav className="container mx-auto px-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <img src={logo} alt="comet Logo" className="h-12 mr-4" />
          <h1 className="text-xl font-bold">comet</h1>
        </div>
        <ul className="flex space-x-4 h-16 items-center">
          <li>
            <NavLink to="/home" className={linkClass}>Home</NavLink>
          </li>
          {isAuthenticated && (
            <>
              <li>
                <NavLink to="/dashboard" className={linkClass}>Dashboard</NavLink>
              </li>
              <li>
                <NavLink to="/pricing" className={linkClass}>Pricing Tool</NavLink>
              </li>
              <li>
                <NavLink to="/designer" className={linkClass}>Designer</NavLink>
              </li>
            </>
          )}
          <li>
            <button
              onClick={() => setDark(!dark)}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle dark mode"
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </li>
          <li>
            {isAuthenticated ? (
              <button onClick={handleLogout} className="bg-teal-600 hover:bg-teal-500 text-white font-bold py-2 px-4 rounded">
                Logout
              </button>
            ) : (
              <button onClick={handleLogin} className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-2 px-4 rounded">
                Login
              </button>
            )}
          </li>
        </ul>
      </div>
    </nav>
  );
};

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
const App = () => {
  const { instance } = useMsal();

  useEffect(() => {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance]);

  return (
    <div className="min-h-screen flex flex-col dark:bg-gray-900 dark:text-white transition-colors">
      <header className="bg-black text-white shadow-md">
        <Navigation />
      </header>

      <main className="flex-grow container mx-auto px-4 py-8">
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<Home />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing/*"
            element={
              <ProtectedRoute>
                <PricingTool />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/designer"
            element={
              <ProtectedRoute>
                <Designer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/security"
            element={
              <ProtectedRoute>
                <ComingSoon title="Security Management" icon="🔒" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance"
            element={
              <ProtectedRoute>
                <ComingSoon title="Performance Insights" icon="⚡" />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

      <footer className="bg-gray-200 dark:bg-gray-800 dark:text-gray-300 py-4">
        <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-600 dark:text-gray-400">
          <p>☄️ comet – Cloud Operations Management &amp; Engineering Tool</p>
          <div className="flex gap-4">
            <a
              href="https://github.com/offprem-cloud/azure-pricing-app"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-teal-600"
            >
              GitHub
            </a>
            <span>v0.1.0</span>
            <span>© {new Date().getFullYear()} offprem-cloud</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
